import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Segmented,
  Select,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import {
  dedupeLeadsByNumber,
  fetchGroupContacts,
  fetchUserGroups,
  markContactsInGroup,
} from './flowLeadsGroupUtils';
import CustomDrawer from 'components/shared-components/CustomDrawer';

const { Text } = Typography;

const GROUP_MODE = {
  CREATE: 'create',
  EXISTING: 'existing',
};

const CreateGroupFromLeadsDrawer = ({
  open,
  onClose,
  fetchLeads,
  sourceLabel,
}) => {
  const { api, authToken, UserData, isMobile } = useMyContext();
  const [step, setStep] = useState(0);
  const [groupMode, setGroupMode] = useState(GROUP_MODE.CREATE);
  const [groupName, setGroupName] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [checkingGroup, setCheckingGroup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadLeads = useCallback(async () => {
    if (!open || !fetchLeads) {
      return;
    }

    setLoadingLeads(true);
    try {
      const leads = await fetchLeads();
      setContacts(dedupeLeadsByNumber(leads));
    } catch (error) {
      console.error('Failed to load leads for group:', error);
      message.error('Failed to load leads');
      setContacts([]);
    } finally {
      setLoadingLeads(false);
    }
  }, [fetchLeads, open]);

  const loadGroups = useCallback(async () => {
    if (!open || !UserData?.id) {
      return;
    }

    setLoadingGroups(true);
    try {
      const data = await fetchUserGroups({
        api,
        authToken,
        userId: UserData.id,
      });
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups:', error);
      message.error('Failed to load groups');
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [api, authToken, open, UserData?.id]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setGroupMode(GROUP_MODE.CREATE);
      setGroupName('');
      setSelectedGroupId(null);
      setContacts([]);
      return;
    }

    loadLeads();
    loadGroups();
  }, [open, loadLeads, loadGroups]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === selectedGroupId) || null,
    [groups, selectedGroupId],
  );

  const newContacts = useMemo(
    () => contacts.filter((contact) => !contact.inGroup),
    [contacts],
  );

  const existingContacts = useMemo(
    () => contacts.filter((contact) => contact.inGroup),
    [contacts],
  );

  const verifyContacts = useMemo(() => {
    if (groupMode !== GROUP_MODE.EXISTING) {
      return contacts;
    }

    return [...contacts].sort((left, right) => Number(left.inGroup) - Number(right.inGroup));
  }, [contacts, groupMode]);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    onClose();
  };

  const removeContact = (key) => {
    setContacts((prev) => prev.filter((item) => item.key !== key));
  };

  const handleModeChange = (mode) => {
    setGroupMode(mode);
    setGroupName('');
    setSelectedGroupId(null);
    setContacts((prev) => prev.map((contact) => ({ ...contact, inGroup: false })));
  };

  const handleNext = async () => {
    if (groupMode === GROUP_MODE.CREATE) {
      const name = groupName.trim();
      if (!name) {
        message.warning('Enter a group name');
        return;
      }
    } else if (!selectedGroupId) {
      message.warning('Select a group');
      return;
    }

    if (contacts.length === 0 && !loadingLeads) {
      message.warning('No numbers available for this selection');
      return;
    }

    if (groupMode === GROUP_MODE.EXISTING && selectedGroupId) {
      setCheckingGroup(true);
      try {
        const groupContacts = await fetchGroupContacts({
          api,
          authToken,
          groupId: selectedGroupId,
        });
        setContacts((prev) => markContactsInGroup(prev, groupContacts));
        setGroupName(selectedGroup?.name || '');
      } catch (error) {
        console.error('Failed to load group contacts:', error);
        message.error('Failed to load group contacts');
        return;
      } finally {
        setCheckingGroup(false);
      }
    }

    setStep(1);
  };

  const handleSubmit = async () => {
    const isCreateMode = groupMode === GROUP_MODE.CREATE;
    const name = isCreateMode ? groupName.trim() : (selectedGroup?.name || groupName).trim();

    if (isCreateMode && !name) {
      message.warning('Enter a group name');
      setStep(0);
      return;
    }

    if (!isCreateMode && !selectedGroupId) {
      message.warning('Select a group');
      setStep(0);
      return;
    }

    const toImport = isCreateMode ? contacts : newContacts;
    if (toImport.length === 0) {
      message.warning(
        isCreateMode
          ? 'Add at least one number'
          : 'No new numbers to add — all selected numbers are already in this group',
      );
      return;
    }

    setSubmitting(true);
    try {
      let groupId = selectedGroupId;

      if (isCreateMode) {
        const createResponse = await apiClient.post(
          `${api}create-group`,
          { user_id: UserData.id, name, description: '' },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );

        groupId = createResponse.data?.group_id;
        if (!groupId) {
          throw new Error('Group id not returned');
        }
      }

      const importedData = toImport.map((contact) => [
        contact.name || null,
        contact.number,
      ]);

      await apiClient.post(
        `${api}import-group/${groupId}`,
        { importedData },
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      if (isCreateMode) {
        message.success(`Group "${name}" created with ${toImport.length} contact${toImport.length === 1 ? '' : 's'}`);
      } else {
        message.success(`${toImport.length} contact${toImport.length === 1 ? '' : 's'} added to "${name}"`);
      }

      onClose();
    } catch (error) {
      console.error('Failed to save group from leads:', error);
      message.error(isCreateMode ? 'Failed to create group' : 'Failed to add contacts to group');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value) => value || '—',
    },
    {
      title: 'Number',
      dataIndex: 'number',
      width: 150,
    },
    ...(groupMode === GROUP_MODE.EXISTING
      ? [{
        title: 'Status',
        dataIndex: 'inGroup',
        render: (inGroup) => (
          inGroup
            ? <Tag>Exist</Tag>
            : <Tag color="green">New</Tag>
        ),
      }]
      : []),
    {
      title: '',
      key: 'actions',
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeContact(row.key)}
        />
      ),
    },
  ];

  const stepBusy = loadingLeads || checkingGroup;

  return (
    <CustomDrawer
      title="Create contact group from leads"
      open={open}
      onClose={handleClose}
      width={'40vw'}
      destroyOnClose
      footer={
        step === 0 ? (
          <div style={{ display: 'flex', gap: 12 }}>
            <Button onClick={handleClose} disabled={submitting} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleNext}
              loading={stepBusy}
              style={{ flex: 1 }}
            >
              Next
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              onClick={() => {
                setContacts((prev) => prev.map((contact) => ({ ...contact, inGroup: false })));
                setStep(0);
              }}
              disabled={submitting}
              style={{ flex: 1 }}
            >
              Back
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={groupMode === GROUP_MODE.CREATE ? contacts.length === 0 : newContacts.length === 0}
              style={{ flex: 1 }}
            >
              {groupMode === GROUP_MODE.CREATE ? 'Create group' : 'Add to group'}
            </Button>
          </div>
        )
      }
    >
      <Steps
        size="small"
        current={step}
        responsive={false}
        style={{ marginBottom: 24 }}
        items={[
          { title: 'Group' },
          { title: 'Verify numbers' },
        ]}
      />

      {step === 0 ? (
        <Form layout="vertical">
          <Form.Item label="Action" style={{ marginBottom: 16 }}>
            <Segmented
              block
              value={groupMode}
              onChange={handleModeChange}
              options={[
                { label: 'Create new', value: GROUP_MODE.CREATE },
                { label: 'Existing group', value: GROUP_MODE.EXISTING },
              ]}
            />
          </Form.Item>

          {groupMode === GROUP_MODE.CREATE ? (
            <Form.Item label="Group name" required>
              <Input
                placeholder="e.g. Contact Form leads — Jul 2026"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={120}
              />
            </Form.Item>
          ) : (
            <Form.Item label="Select group" required>
              <Select
                showSearch
                allowClear
                placeholder="Choose a group"
                value={selectedGroupId}
                onChange={setSelectedGroupId}
                loading={loadingGroups}
                optionFilterProp="label"
                options={groups.map((group) => ({
                  value: group.id,
                  label: group.name,
                }))}
              />
            </Form.Item>
          )}

          {!loadingLeads && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {contacts.length} unique number{contacts.length === 1 ? '' : 's'} from leads
              {groupMode === GROUP_MODE.EXISTING && selectedGroupId
                ? ' — overlap with the group is checked on the next step'
                : groupMode === GROUP_MODE.EXISTING
                  ? ' — select a group to continue'
                  : ' will be added'}
            </Text>
          )}
        </Form>
      ) : (
        <Spin spinning={stepBusy}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
            {groupMode === GROUP_MODE.EXISTING ? (
              <>
                <Tag color="green" style={{ marginRight: 8 }}>New: {newContacts.length}</Tag>
                <Tag style={{ marginRight: 8 }}>Exist: {existingContacts.length}</Tag>
              </>
            ) : (
              <>Remove any numbers you do not want in the group ({contacts.length} selected)</>
            )}
          </Text>
          <Table
            size="small"
            rowKey="key"
            columns={columns}
            dataSource={verifyContacts}
            pagination={{ pageSize: isMobile ? 5 : 10, showSizeChanger: false }}
          />
        </Spin>
      )}
    </CustomDrawer>
  );
};

export default CreateGroupFromLeadsDrawer;
